/**
 * The `/settings` endpoints.
 *
 * Two things here are not boilerplate:
 *
 *  - **`useAgentStatus` is a query, not part of the settings payload.** It reads
 *    process environment, not the database, so it can change under you without
 *    any mutation on this page having happened (an App Setting edited in the
 *    portal, a redeploy). A 60 s refetch keeps the two send locks honest.
 *
 *  - **Every mutation invalidates BOTH the settings queries and the auth
 *    profile.** A role change is the one kind of edit that can change what the
 *    person making it is allowed to see — `AuthContext` fetches the profile once
 *    per page load, so without an explicit refresh the sidebar keeps showing
 *    pages the server will now refuse. `Settings.tsx` calls `refreshProfile()`
 *    when the edited address is the signed-in user's.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  AgentStatus,
  MatrixUpdateResult,
  PermissionMatrix,
  SettingsRole,
  SettingsUser,
  UsersUpdateResult,
} from '../types/domain';

const SETTINGS_KEY = ['settings'] as const;

export function useSettingsUsers() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'users'],
    queryFn: () => api.get<SettingsUser[]>('/api/v1/settings/users'),
  });
}

export function useSettingsRoles() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'roles'],
    queryFn: () => api.get<SettingsRole[]>('/api/v1/settings/roles'),
  });
}

export function usePermissionMatrix() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'permission-matrix'],
    queryFn: () => api.get<PermissionMatrix>('/api/v1/settings/permission-matrix'),
  });
}

export function useAgentStatus() {
  return useQuery({
    queryKey: ['agent', 'status'],
    queryFn: () => api.get<AgentStatus>('/api/v1/agent/status'),
    refetchInterval: 60_000,
  });
}

export interface UserRoleChange {
  email: string;
  roleId: number;
  action: 'assign' | 'remove';
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserRoleChange) => api.post<UsersUpdateResult>('/api/v1/settings/users', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      qc.invalidateQueries({ queryKey: ['auth', 'profile'] });
    },
  });
}

export interface MatrixCellChange {
  roleId: number;
  pageId: number;
  canView: boolean;
  canEdit: boolean;
}

export function useUpdatePermissionMatrix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MatrixCellChange) => api.put<MatrixUpdateResult>('/api/v1/settings/permission-matrix', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      qc.invalidateQueries({ queryKey: ['auth', 'profile'] });
    },
  });
}
